import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ShippingMethod } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

@Injectable({ providedIn: 'root' })
export class ShippingMethodService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/shipping-methods`;
  }

  /**
   * Normaliza el status de mayúsculas a minúsculas (ACTIVE -> active)
   */
  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend
   */
  private mapToShippingMethod(item: any): ShippingMethod {
    return {
      id: item.id?.toString() || '',
      code: item.code || '',
      name: item.name || '',
      shipping: item.shipping ?? false,
      delivery: item.delivery ?? false,
      status: this.normalizeStatus(item.status),
    };
  }

  /**
   * Obtiene todos los métodos de envío (con paginación del backend)
   */
  getAll(
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
  ): Observable<ShippingMethod[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    return this.httpClient
      .get<ApiResponse<PageableData>>(this.apiUrl, { params })
      .pipe(
        map((response) => {
          // La estructura es: response.data.content
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return items.map((item: any) => this.mapToShippingMethod(item));
        }),
      );
  }

  /**
   * Obtiene un método de envío por ID
   */
  getById(id: string): Observable<ShippingMethod> {
    return this.httpClient
      .get<ApiResponse<any>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => this.mapToShippingMethod(response.data)));
  }

  /**
   * Crea un nuevo método de envío
   * La API devuelve solo { message: "..." } sin data
   */
  create(data: Omit<ShippingMethod, 'id'>): Observable<{ message: string }> {
    return this.httpClient
      .post<{ success: boolean; message: string }>(this.apiUrl, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Método de envío creado exitosamente',
        })),
      );
  }

  /**
   * Actualiza un método de envío existente
   * La API devuelve solo { message: "..." } sin data
   */
  update(
    id: string,
    data: Partial<ShippingMethod>,
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`, data)
      .pipe(
        map((response) => ({
          message:
            response.message || 'Método de envío actualizado exitosamente',
        })),
      );
  }

  /**
   * Elimina un método de envío
   * La API devuelve solo { message: "..." } sin data
   */
  delete(id: string): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Método de envío eliminado exitosamente',
        })),
      );
  }

  /**
   * Obtiene el historial de acciones de un método de envío
   */
  getHistory(
    id: string,
    page = 0,
    size = 20,
  ): Observable<ShippingMethodHistoryAction[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.httpClient
      .get<
        ApiResponse<PageableData>
      >(`${this.apiUrl}/${id}/history`, { params })
      .pipe(
        map((response) => {
          const items = response.data?.content || [];
          return items.map((action: any) => ({
            id: action.id,
            date: action.date,
            action: action.action || 'Acción',
            user: action.user || 'Usuario',
            ip: action.ip || '',
            device: action.device || '',
            browser: action.browser || '',
            location: action.location || '',
          }));
        }),
      );
  }

  /**
   * Actualiza el status de un método de envío
   * La API devuelve solo { message: "..." } sin data
   */
  updateStatus(
    id: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${id}/status`, { status })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }

  /**
   * Exporta la lista de métodos de envío a PDF
   * La API genera y descarga el PDF directamente
   */
  exportPdf(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de formas de envio')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/shippingmethod`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta la lista de métodos de envío a Excel
   * La API genera y descarga el archivo Excel directamente
   */
  exportExcel(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de formas de envio')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/shippingmethod`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta el historial de un método de envío a PDF
   * GET /api/export/pdf/history/shippingmethod/{id}?title=...
   */
  exportHistoryPdf(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial formas de envio',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/history/shippingmethod/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta el historial de un método de envío a Excel
   * GET /api/export/excel/history/shippingmethod/{id}?title=...
   */
  exportHistoryExcel(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial formas de envio',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/history/shippingmethod/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }
}

// Interface for history actions
export interface ShippingMethodHistoryAction {
  id: number;
  date: Date;
  action: string;
  user: string;
  ip: string;
  device: string;
  browser: string;
  location: string;
}
